import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, unref as _unref } from "vue"

const _hoisted_1 = { class: "moments-list__container section__container" }
const _hoisted_2 = { class: "moments-list__list section__content section__content--listing section__content--thirds" }
const _hoisted_3 = ["innerHTML"]
const _hoisted_4 = {
  key: 1,
  class: "site-page-partial__no-image-container"
}
const _hoisted_5 = { class: "site-page-partial__text-container" }
const _hoisted_6 = {
  key: 0,
  class: "site-page-partial__meta"
}
const _hoisted_7 = {
  key: 0,
  class: "site-page-partial__meta-categories tag-container"
}
const _hoisted_8 = {
  key: 1,
  class: "site-page-partial__year"
}
const _hoisted_9 = { class: "site-page-partial__heading heading heading--small" }
const _hoisted_10 = ["href"]
const _hoisted_11 = {
  key: 1,
  class: "site-page-partial__excerpt"
}
const _hoisted_12 = {
  key: 0,
  class: "moments-list__show-more show-more"
}
const _hoisted_13 = { class: "moments-list__show-more-text" }
const _hoisted_14 = {
  key: 1,
  class: "moments-list__empty no-content"
}

import { computed, ref } from "vue";
import type { ITimelineMapItem } from "@features/Pages/TimelineMapPage/_js/app/interfaces/ITimelineMapItem";
import { useTimelineMapStore } from "@features/Pages/TimelineMapPage/_js/app/store/timelineMapStore";

const initialVisibleMoments = 9;
const momentsBatchSize = 9;

export default /*@__PURE__*/_defineComponent({
  __name: 'MomentsList',
  setup(__props) {

const store = useTimelineMapStore();
const visibleCount = ref(initialVisibleMoments);

const currentLanguage = (
  document.getElementById("js-current-language") as HTMLInputElement | null
)?.value ?? document.documentElement.lang ?? "sv";

const collator = new Intl.Collator(
  currentLanguage.startsWith("en") ? "en" : "sv",
  { sensitivity: "base" }
);

const sectionClasses = computed(() => {
  if (store.isVRAK) {
    return ["section--site-page-partial-color-black-100-vrak"];
  }

  if (store.isMM) {
    return ["section--site-page-partial-color-bremon-100-mm"];
  }

  if (store.isJVM) {
    return ["section--site-page-partial-color-light-green-blue"];
  }

  return [];
});

const useLinkStyle = computed(() => store.isMM || store.isVRAK);

const showMoreButtonClasses = computed(() => [
  "moments-list__show-more-button",
  useLinkStyle.value ? "link" : "button button--secondary",
]);

const showMoreIconClasses = computed(() => [
  useLinkStyle.value ? "link__icon" : "button__icon",
  "icon",
  "icon--plus",
]);

const listedMoments = computed(() => {
  const momentsById = new Map<number, ITimelineMapItem>();

  store.epochs
    .flatMap((epoch) => epoch.timelineMapItems ?? [])
    .filter((item) => item.type === "Moment")
    .forEach((item) => {
      momentsById.set(item.id, item);
    });

  return Array.from(momentsById.values())
    .sort((left, right) => collator.compare(left.title ?? "", right.title ?? ""));
});

const visibleMoments = computed(() => listedMoments.value.slice(0, visibleCount.value));

const remainingCount = computed(() => Math.max(listedMoments.value.length - visibleCount.value, 0));

const fallbackShowMoreText = currentLanguage.startsWith("en") ? "Show more" : "Visa fler";

const showMoreText = computed(() => {
  const translation = store.translations.showMore;
  return typeof translation === "string" && translation.trim().length > 0
    ? translation
    : fallbackShowMoreText;
});

const showMoreLabel = computed(() => {
  const parts = showMoreText.value.trim().split(/\s+/);

  if (parts.length >= 2) {
    return `${parts[0]} ${remainingCount.value} ${parts.slice(1).join(" ")}`;
  }

  return `${showMoreText.value} ${remainingCount.value}`;
});

function onCardClick(event: MouseEvent) {
  if (!(event.currentTarget instanceof HTMLElement)) {
    return;
  }

  if (event.target instanceof Element && event.target.closest(".site-page-partial__link")) {
    return;
  }

  const link = event.currentTarget.querySelector(".site-page-partial__link");
  const url = link?.getAttribute("href");

  if (!url || url === "#") {
    return;
  }

  window.location.assign(url);
}

function showMoreMoments() {
  visibleCount.value = Math.min(visibleCount.value + momentsBatchSize, listedMoments.value.length);
}

function getMomentCategories(moment: ITimelineMapItem): string[] {
  if (Array.isArray(moment.categories) && moment.categories.length) {
    return moment.categories.filter(
      (category): category is string => typeof category === "string" && category.trim().length > 0
    );
  }

  if (typeof moment.category === "string" && moment.category.trim().length > 0) {
    return [moment.category];
  }

  return [];
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("section", {
    class: _normalizeClass([sectionClasses.value, "moments-list section section--bg-color-primary"])
  }, [
    _createElementVNode("div", _hoisted_1, [
      (listedMoments.value.length)
        ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
            _createElementVNode("ul", _hoisted_2, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(visibleMoments.value, (moment) => {
                return (_openBlock(), _createElementBlock("li", {
                  key: moment.urlSegment ?? moment.id,
                  class: "moments-list__item site-page-partial section__item"
                }, [
                  _createElementVNode("article", {
                    class: "site-page-partial__content",
                    onClick: onCardClick
                  }, [
                    (moment.imageHtml)
                      ? (_openBlock(), _createElementBlock("figure", {
                          key: 0,
                          class: "site-page-partial__image-container",
                          innerHTML: moment.imageHtml
                        }, null, 8 /* PROPS */, _hoisted_3))
                      : (_openBlock(), _createElementBlock("div", _hoisted_4, [...(_cache[0] || (_cache[0] = [
                          _createElementVNode("span", { class: "site-page-partial__no-image-container-graphic" }, null, -1 /* CACHED */)
                        ]))])),
                    _createElementVNode("div", _hoisted_5, [
                      (getMomentCategories(moment).length || moment.year)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                            (getMomentCategories(moment).length)
                              ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(getMomentCategories(moment), (category) => {
                                    return (_openBlock(), _createElementBlock("span", {
                                      key: category,
                                      class: "site-page-partial__meta-category tag tag--small"
                                    }, _toDisplayString(category), 1 /* TEXT */))
                                  }), 128 /* KEYED_FRAGMENT */))
                                ]))
                              : _createCommentVNode("v-if", true),
                            (moment.year)
                              ? (_openBlock(), _createElementBlock("div", _hoisted_8, _toDisplayString(moment.year), 1 /* TEXT */))
                              : _createCommentVNode("v-if", true)
                          ]))
                        : _createCommentVNode("v-if", true),
                      _createElementVNode("h3", _hoisted_9, [
                        _createElementVNode("a", {
                          class: "site-page-partial__link",
                          href: moment.url || '#'
                        }, _toDisplayString(moment.title), 9 /* TEXT, PROPS */, _hoisted_10)
                      ]),
                      (moment.momentExcerpt)
                        ? (_openBlock(), _createElementBlock("p", _hoisted_11, _toDisplayString(moment.momentExcerpt), 1 /* TEXT */))
                        : _createCommentVNode("v-if", true),
                      _cache[1] || (_cache[1] = _createElementVNode("div", { class: "site-page-partial__arrow-icon-container" }, [
                        _createElementVNode("span", { class: "site-page-partial__arrow-icon icon icon--arrow-right" })
                      ], -1 /* CACHED */))
                    ])
                  ])
                ]))
              }), 128 /* KEYED_FRAGMENT */))
            ]),
            (remainingCount.value > 0)
              ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
                  _createElementVNode("button", {
                    type: "button",
                    class: _normalizeClass(showMoreButtonClasses.value),
                    onClick: showMoreMoments
                  }, [
                    _createElementVNode("span", {
                      class: _normalizeClass(showMoreIconClasses.value)
                    }, null, 2 /* CLASS */),
                    _createElementVNode("span", _hoisted_13, _toDisplayString(showMoreLabel.value), 1 /* TEXT */)
                  ], 2 /* CLASS */)
                ]))
              : _createCommentVNode("v-if", true)
          ], 64 /* STABLE_FRAGMENT */))
        : (_openBlock(), _createElementBlock("div", _hoisted_14, [
            _createElementVNode("p", null, _toDisplayString(_unref(store).translations.noPages), 1 /* TEXT */)
          ]))
    ])
  ], 2 /* CLASS */))
}
}

})